import {css, StyleSheet} from 'aphrodite';
import React from 'react';

import WorkspaceItemInaccessibleGuard from 'auth/WorkspaceItemInaccessibleGuard';
import ModelTypeConstants from 'constants/ModelTypeConstants';
import {FormDefinitionField, FormDefinitionContent} from 'forms/form-designer/Constants';
import FormInstanceHeader from 'forms/form-instance/FormInstanceHeader';
import {
  FormInstanceQuery,
  FormInstanceQueryVariables,
} from 'forms/form-instance/graphql/__generated/FormInstanceQuery';
import {FORM_INSTANCE_QUERY} from 'forms/form-instance/graphql/GetFormDefinition';
import {QueryResultType, useBenchlingQuery} from 'graphql-client/BenchlingQuery';
import ExtractByTypename from 'graphql-client/ExtractByTypename';
import {isInaccessible} from 'graphql-client/InaccessibleInfo';
import notifications from 'notifications';
import InlineBanner, {InlineBannerType} from 'ui-library/banners/InlineBanner';
import makeUseGlobalClientState from 'ui-library/hooks/global-client-state/makeUseGlobalClientState';
import {CenteredHorizontalThrobber} from 'ui-library/HorizontalThrobber';
import TabLayout, {TabLayoutBackgroundColor} from 'ui-library/TabLayout';
import {withLoadingScreen} from 'util/Promises';

import {FormSubmission, FormInstanceState} from './Constants';
import {FormInstanceContents} from './FormInstanceContents';
import {submitFormSubmission} from './FormInstanceDAO';
import {convertFormInstanceStateToFormSubmission} from './FormInstanceState';
import {gatherFormFieldsFromDefinitionContent} from './util';

type FormDefinition = ExtractByTypename<FormInstanceQuery['formDefinition'], 'FormDefinitionNode'>;

interface FormInstanceProps {
  formDefinition: FormDefinition;
}

interface QueryingFormInstanceProps {
  formDefinitionId: string;
}

export const formInstanceGlobalState = makeUseGlobalClientState<FormInstanceState | undefined>(undefined);
export const isFirstMountGlobalState = makeUseGlobalClientState<boolean>(true);
export const isFormSubmittableGlobalState = makeUseGlobalClientState<boolean>(false);
export const isFormSubmittedGlobalState = makeUseGlobalClientState<boolean>(false);

export function FormInstance({formDefinition}: FormInstanceProps): JSX.Element {
  // TODO: BNCH-52639 update identifier so multiple instances of the same form can be open
  const [formInstanceState] = formInstanceGlobalState.useGlobalClientState(formDefinition.id);
  const [isFormSubmittable, setIsFormSubmittable] = isFormSubmittableGlobalState.useGlobalClientState(
    formDefinition.id
  );
  const [isFormSubmitted, setIsFormSubmitted] = isFormSubmittedGlobalState.useGlobalClientState(
    formDefinition.id
  );

  const formDefinitionContent = formDefinition.latestFormDefinitionVersion
    .definition as FormDefinitionContent;

  const initializeIsFormSubmittable = (formDefinition: FormDefinition): boolean =>
    gatherFormFieldsFromDefinitionContent(formDefinitionContent).every(
      (fieldDefinition: FormDefinitionField) => !fieldDefinition.isRequired
    );

  const submitForm = async (): Promise<void> => {
    if (!isFormSubmittable || isFormSubmitted) {
      return;
    }
    const formSubmission: FormSubmission = {
      fieldValues: convertFormInstanceStateToFormSubmission(formInstanceState!),
    };

    await withLoadingScreen(
      submitFormSubmission(formSubmission, formDefinition.latestFormDefinitionVersion.id),
      'Submitting...'
    );
    setIsFormSubmitted(true);
    setIsFormSubmittable(initializeIsFormSubmittable(formDefinition));
  };

  return (
    <TabLayout
      backgroundColor={TabLayoutBackgroundColor.GRAY_LIGHTER}
      header={
        <FormInstanceHeader
          name={formDefinition.name}
          disableSubmit={!isFormSubmittable}
          hideSubmit={isFormSubmitted}
          onSubmit={submitForm}
        />
      }
      isHeaderFrozen={true}
    >
      <div className={css(styles.bodyWrapper)}>
        {!isFormSubmitted ? (
          <FormInstanceContents
            formDefinitionContent={formDefinition.latestFormDefinitionVersion.definition}
            formInstanceTableState={{}}
            onUpdateFormInstanceTableState={() => {}}
            fieldKeyToTableColumnIndex={{}}
          />
        ) : (
          <InlineBanner
            bannerType={InlineBannerType.SUCCESS}
            text={`Successfully submitted ${formDefinition.name}.`}
          />
        )}
      </div>
    </TabLayout>
  );
}

export default function QueryingFormInstance(props: QueryingFormInstanceProps): JSX.Element | null {
  const result = useBenchlingQuery<FormInstanceQuery, FormInstanceQueryVariables>(FORM_INSTANCE_QUERY, {
    variables: {formDefinitionId: props.formDefinitionId},
    fetchPolicy: 'cache-and-network',
    renderCachedDataWhenRefetching: true,
  });

  if (result.type === QueryResultType.PENDING) {
    return <CenteredHorizontalThrobber />;
  } else if (result.type === QueryResultType.ERROR) {
    notifications.error('Could not load form; try reloading the page.');
    return null;
  }

  const {formDefinition} = result.data;
  return !isInaccessible(formDefinition) ? (
    <FormInstance formDefinition={formDefinition} />
  ) : (
    <WorkspaceItemInaccessibleGuard
      inaccessibleInfo={formDefinition}
      modelType={ModelTypeConstants.FORM_DEFINITION}
    />
  );
}

const styles = StyleSheet.create({
  bodyWrapper: {
    paddingTop: 28,
    paddingLeft: 215,
    paddingRight: 215,
  },
});
